import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ShoppingCart, Check, ArrowLeft, Lock, Bell, AlertTriangle, Heart, Scale } from 'lucide-react';
import ProductReviews from '../components/ProductReviews';
import StarRating from '../components/StarRating';
import StructuredData, { buildProductSchema, buildBreadcrumbSchema } from '../components/StructuredData';
import SEO from '../components/SEO';
import CrossSell from '../components/CrossSell';
import RecentlyViewed from '../components/RecentlyViewed';
import { pushRecentlyViewed } from '../utils/recentlyViewed';
import { useWishlist } from '../context/WishlistContext';
import { useCompare } from '../context/CompareContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { addToCart, currency, applyCoupon, coupon } = useCart();
  const { isInWishlist, toggle: toggleWishlist } = useWishlist();
  const { has: hasCompare, toggle: toggleCompare } = useCompare();
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(5);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyQty, setNotifyQty] = useState('');
  const [notifyStatus, setNotifyStatus] = useState(null); // 'sent' | 'already' | 'error' | null
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [autoCouponStatus, setAutoCouponStatus] = useState(null); // 'applied' | 'invalid' | null
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      const step = Number(product.min_order_step_m || 5);
      setQuantity((q) => {
        if (!q || q < step) return step;
        return q;
      });
      pushRecentlyViewed(product);
    }
  }, [product?.id]);

  // Real-time price recalc from backend (single source of truth).
  useEffect(() => {
    if (!product || !quantity || quantity <= 0) {
      setPricing(null);
      return;
    }
    const ctrl = new AbortController();
    const tid = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/pricing/calculate`,
          { product_id: product.id, quantity: Number(quantity), currency },
          { signal: ctrl.signal },
        );
        setPricing(res.data);
      } catch (e) {
        if (e.name !== 'CanceledError') setPricing(null);
      }
    }, 200);
    return () => { clearTimeout(tid); ctrl.abort(); };
  }, [product?.id, quantity, currency]);

  // Auto-apply coupon from ?coupon= url param (back-in-stock link)
  useEffect(() => {
    const code = searchParams.get('coupon');
    if (!code) return;
    if (coupon?.code?.toUpperCase() === code.toUpperCase()) {
      setAutoCouponStatus('applied');
      return;
    }
    (async () => {
      const res = await applyCoupon(code, user?.email);
      setAutoCouponStatus(res.ok ? 'applied' : 'invalid');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user?.email]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTranslation = (obj, field) => {
    return obj?.translations?.[getUiLang(i18n)]?.[field] || obj?.translations?.['sk']?.[field] || '';
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!notifyEmail || !notifyEmail.includes('@')) return;
    setNotifySubmitting(true);
    try {
      const res = await axios.post(`${API}/products/${id}/notify`, {
        email: notifyEmail.trim(),
        language: i18n.language,
        requested_quantity: notifyQty ? Number(notifyQty) : 0,
      });
      setNotifyStatus(res.data.status === 'already_subscribed' ? 'already' : 'sent');
      setNotifyEmail('');
      setNotifyQty('');
    } catch (err) {
      setNotifyStatus('error');
    } finally {
      setNotifySubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-2xl text-orange-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">{t('common.error')}</h1>
        <Link to="/catalog" className="text-orange-500 hover:text-orange-600">
          {t('cart.continue_shopping')}
        </Link>
      </div>
    );
  }

  const price = currency === 'EUR' ? product.price_eur : product.price_usd;
  const discountedPrice = price * (1 - product.discount / 100);
  const isMb = (product.unit || 'mb') === 'mb';
  const unitLabel = isMb ? t('product.unit_mb', 'bm') : (t('product.pcs', 'ks'));
  const minStep = Number(product.min_order_step_m || (isMb ? 5 : 1));
  const stockUnits = Number(product.stock || 0);
  const reservedUnits = Number(product.reserved || 0);
  const availableUnits = Math.max(0, stockUnits - reservedUnits);
  const bulkThreshold = Number(product.bulk_threshold_m || 30);
  const bulkPct = Number(product.bulk_discount_percent || 20);
  const validationError = pricing?.validation_error;
  const finalTotal = Number(pricing?.final_total || 0);
  const baseTotal = Number(pricing?.base_total || 0);
  const discountAmount = Number(pricing?.discount_amount || 0);
  const currencySymbol = currency === 'EUR' ? '\u20AC' : '$';

  // Schema.org JSON-LD for SEO / Google rich results
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const currentLang = getUiLang(i18n);
  const productName = (product.translations?.[currentLang] || product.translations?.sk || {}).name || product.sku;
  const productDescription = (product.translations?.[currentLang] || product.translations?.sk || {}).description || productName;
  const productImage = (product.images && product.images[0])
    ? (product.images[0].startsWith('http') ? product.images[0] : `${siteUrl}${product.images[0]}`)
    : null;
  const structuredData = [
    buildProductSchema(product, { language: currentLang, siteUrl }),
    buildBreadcrumbSchema([
      { name: 'Sunify', url: '/' },
      { name: t('nav.catalog', 'Katalóg'), url: '/catalog' },
      { name: productName, url: `/product/${product.id}` },
    ], { siteUrl }),
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={productName}
        description={productDescription.slice(0, 160)}
        image={productImage}
        url={`/product/${product.id}`}
        type="product"
      />
      <StructuredData data={structuredData} />
      <div className="container mx-auto px-4 py-8">
        {autoCouponStatus === 'applied' && coupon && (
          <div
            className="mb-6 flex items-center gap-3 rounded-xl border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50 p-4"
            data-testid="auto-coupon-banner"
          >
            <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
              %
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900">
                {t('product.coupon_applied_title', { percent: coupon.discount_percent })}
              </p>
              <p className="text-sm text-gray-600">
                {t('product.coupon_applied_subtitle')}{' '}
                <span className="font-mono font-bold bg-gray-900 text-orange-300 px-2 py-0.5 rounded">{coupon.code}</span>
              </p>
            </div>
          </div>
        )}

        {/* Back Button */}
        <Link
          to="/catalog"
          className="inline-flex items-center space-x-2 text-orange-500 hover:text-orange-600 mb-6"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>{t('cart.continue_shopping')}</span>
        </Link>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Product Image */}
            <div>
              <div className="relative">
                <img
                  src={product.images[0]}
                  alt={getTranslation(product, 'name')}
                  className="w-full rounded-lg"
                  data-testid="product-image"
                />
                {product.discount > 0 && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full text-lg font-bold">
                    -{product.discount}%
                  </div>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div>
              <h1 className="text-3xl font-bold mb-4" data-testid="product-name">
                {getTranslation(product, 'name')}
              </h1>

              {/* Rating display */}
              {product.rating_count > 0 && (
                <div className="mb-3" data-testid="product-rating-summary">
                  <a href="#reviews" className="inline-flex items-center gap-2 hover:underline">
                    <StarRating value={product.rating_avg || 0} size="h-4 w-4" />
                    <span className="text-sm text-gray-600">
                      {Number(product.rating_avg || 0).toFixed(1)} ({product.rating_count} {t('reviews.reviews_short')})
                    </span>
                  </a>
                </div>
              )}
              
              <div className="mb-6">
                <p className="text-gray-600 text-lg">
                  {getTranslation(product, 'description')}
                </p>
              </div>

              {/* Price */}
              <div className="mb-6" data-testid="product-price-section">
                {isAuthenticated ? (
                  <div>
                    {product.discount > 0 ? (
                      <div>
                        <div className="text-4xl font-bold text-orange-500">
                          {discountedPrice.toFixed(2)} {currency}
                          <span className="text-lg font-normal text-gray-500 ml-2">/ {unitLabel}</span>
                        </div>
                        <div className="text-xl text-gray-500 line-through">
                          {price.toFixed(2)} {currency} / {unitLabel}
                        </div>
                      </div>
                    ) : (
                      <div className="text-4xl font-bold text-orange-500">
                        {price.toFixed(2)} {currency}
                        <span className="text-lg font-normal text-gray-500 ml-2">/ {unitLabel}</span>
                      </div>
                    )}
                    {isMb && bulkThreshold > 0 && bulkPct > 0 && (
                      <div className="mt-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-block" data-testid="bulk-discount-hint">
                        {t('product.bulk_hint', `Pri každých {{qty}} mb získate zľavu {{pct}}%`, { qty: bulkThreshold, pct: bulkPct })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Lock className="h-8 w-8 text-orange-500" />
                      <div>
                        <div className="text-xl font-bold text-orange-500">Cena dostupna po prihlaseni</div>
                        <p className="text-sm text-gray-600 mt-1">Prihlaste sa pre zobrazenie cien a nakup</p>
                      </div>
                    </div>
                    <Link
                      to="/login"
                      className="w-full block text-center px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
                    >
                      Prihlasit sa
                    </Link>
                  </div>
                )}
              </div>

              {/* Stock */}
              <div className="mb-6">
                {availableUnits >= minStep ? (
                  <div className="flex items-center space-x-2 text-green-600">
                    <Check className="h-5 w-5" />
                    <span data-testid="stock-available">
                      {isMb
                        ? `${t('product.in_stock', 'Skladom')}: ${availableUnits.toFixed(0)} ${unitLabel}`
                        : t('product.in_stock_pcs', { count: Math.floor(availableUnits) })}
                    </span>
                  </div>
                ) : availableUnits > 0 && availableUnits < minStep ? (
                  <div className="flex items-center space-x-2 text-yellow-600">
                    <span className="font-semibold">{t('product.check_with_seller')}</span>
                  </div>
                ) : (
                  <div className="text-red-500 font-semibold">
                    {t('product.on_order')}
                  </div>
                )}
                {isMb && availableUnits >= minStep && availableUnits <= bulkThreshold && (
                  <div
                    className="mt-3 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700"
                    data-testid="low-stock-banner"
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{t('product.low_stock_warning_mb', 'Posledné {{count}} mb na sklade', { count: availableUnits.toFixed(0) })}</span>
                  </div>
                )}
                {availableUnits === 0 && (
                  <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4" data-testid="notify-stock-block">
                    <div className="flex items-start gap-2 mb-2">
                      <Bell className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">{t('product.notify_title')}</p>
                        <p className="text-sm text-gray-600">{t('product.notify_subtitle')}</p>
                      </div>
                    </div>
                    {notifyStatus === 'sent' && (
                      <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2" data-testid="notify-success">
                        {t('product.notify_success')}
                      </p>
                    )}
                    {notifyStatus === 'already' && (
                      <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2" data-testid="notify-already">
                        {t('product.notify_already')}
                      </p>
                    )}
                    {notifyStatus === 'error' && (
                      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="notify-error">
                        {t('product.notify_error')}
                      </p>
                    )}
                    {notifyStatus !== 'sent' && notifyStatus !== 'already' && (
                      <form onSubmit={handleNotifySubmit} className="flex flex-col gap-2 mt-2">
                        <div className="flex gap-2">
                          <input
                            type="email"
                            required
                            value={notifyEmail}
                            onChange={(e) => setNotifyEmail(e.target.value)}
                            placeholder={t('product.notify_placeholder')}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            data-testid="notify-email-input"
                          />
                          <input
                            type="number"
                            min="0"
                            step={minStep || 1}
                            value={notifyQty}
                            onChange={(e) => setNotifyQty(e.target.value)}
                            placeholder={`${t('product.quantity', 'Mnozstvo')} (${unitLabel})`}
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            data-testid="notify-qty-input"
                          />
                          <button
                            type="submit"
                            disabled={notifySubmitting}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:bg-gray-300"
                            data-testid="notify-submit-button"
                          >
                            {notifySubmitting ? '…' : t('product.notify_submit')}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">
                          {t('product.notify_qty_hint', 'Voliteľné: zadajte požadované množstvo, pomôže nám naplánovať dodanie.')}
                        </p>
                      </form>
                    )}
                  </div>
                )}
              </div>

              {/* Quantity Selector - only for authenticated users */}
              {isAuthenticated && availableUnits >= minStep && (
                <div className="mb-6">
                  <label htmlFor="product-quantity-input" className="block text-sm font-semibold mb-2">
                    {t('product.quantity', 'Mnozstvo')} ({unitLabel}):
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(minStep, Number(q || minStep) - minStep))}
                      className="w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-lg font-semibold"
                      data-testid="decrease-quantity"
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <input
                      id="product-quantity-input"
                      type="number"
                      inputMode="numeric"
                      min={minStep}
                      max={availableUnits}
                      step={minStep}
                      value={quantity}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') { setQuantity(''); return; }
                        const parsed = parseFloat(raw);
                        if (Number.isNaN(parsed)) return;
                        setQuantity(parsed);
                      }}
                      onBlur={() => {
                        const q = Number(quantity || 0);
                        if (!q || q < minStep) { setQuantity(minStep); return; }
                        if (q > availableUnits) { setQuantity(availableUnits); return; }
                        // «нормализуем» к ближайшему кратному minStep вниз
                        if (q % minStep !== 0) setQuantity(Math.floor(q / minStep) * minStep);
                      }}
                      className="w-24 h-10 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      data-testid="quantity-input"
                    />
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(availableUnits, Number(q || 0) + minStep))}
                      className="w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-lg font-semibold"
                      data-testid="increase-quantity"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                    <span className="text-sm text-gray-500">
                      max {availableUnits.toFixed(0)} {unitLabel}
                    </span>
                  </div>
                  {isMb && (
                    <p className="text-xs text-gray-500 mt-2" data-testid="quantity-step-hint">
                      {t('product.step_hint', 'Plénka sa odpredáva po {{step}} mb (napr. {{ex1}}, {{ex2}}, {{ex3}}...)', { step: minStep, ex1: minStep, ex2: minStep*2, ex3: minStep*3 })}
                    </p>
                  )}
                  {validationError && (
                    <p className="text-xs text-red-600 mt-1" data-testid="quantity-error">
                      {validationError.startsWith('quantity_not_multiple_of')
                        ? t('product.error_not_multiple', `Hodnota musí byť násobkom ${minStep}`)
                        : validationError === 'quantity_must_be_positive'
                          ? t('product.error_positive', 'Zadajte kladné množstvo')
                          : validationError.startsWith('quantity_below_min_step')
                            ? t('product.error_below_min', `Minimálne ${minStep} ${unitLabel}`)
                            : validationError === 'quantity_exceeds_stock'
                              ? t('product.error_exceeds_stock', 'Nedostatok na sklade')
                              : validationError}
                    </p>
                  )}
                  {pricing && !validationError && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm" data-testid="line-total-preview">
                      <div className="flex justify-between text-gray-700">
                        <span>{quantity} {unitLabel} × {price.toFixed(2)} {currencySymbol}</span>
                        <span>{baseTotal.toFixed(2)} {currencySymbol}</span>
                      </div>
                      {discountAmount > 0 && (
                        <div className="flex justify-between text-emerald-700">
                          <span>{t('product.bulk_discount_label', { pct: bulkPct, qty: bulkThreshold, defaultValue: `Zľava (-${bulkPct}% za každých ${bulkThreshold} mb)` })}</span>
                          <span>−{discountAmount.toFixed(2)} {currencySymbol}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-gray-900 mt-1 pt-2 border-t border-gray-200">
                        <span>{t('product.line_total', 'Spolu')}</span>
                        <span data-testid="line-final-total">{finalTotal.toFixed(2)} {currencySymbol}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Add to Cart Button */}
              {isAuthenticated ? (
                <button
                  onClick={handleAddToCart}
                  disabled={availableUnits < minStep || added || !!validationError || !quantity}
                  className="w-full px-8 py-4 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg"
                  data-testid="add-to-cart-button"
                >
                  {added ? (
                    <>
                      <Check className="h-6 w-6" />
                      <span>Pridané do košíka!</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-6 w-6" />
                      <span>{t('product.add_to_cart')}</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="w-full block text-center px-8 py-4 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition text-lg"
                  >
                    {t('auth.login_to_buy')}
                  </Link>
                  <Link
                    to="/register"
                    className="w-full block text-center px-8 py-4 border-2 border-orange-500 text-orange-500 rounded-lg font-semibold hover:bg-orange-50 transition text-lg"
                  >
                    Registrovať sa
                  </Link>
                </div>
              )}

              {/* Wishlist + Compare quick actions */}
              <div className="mt-4 flex items-center gap-3">
                {isAuthenticated && (
                  <button
                    onClick={() => toggleWishlist(id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
                      isInWishlist(id)
                        ? 'bg-red-50 border-red-200 text-red-600'
                        : 'border-gray-300 text-gray-700 hover:border-red-200 hover:text-red-500'
                    }`}
                    data-testid="product-wishlist-toggle"
                  >
                    <Heart className={`h-5 w-5 ${isInWishlist(id) ? 'fill-red-500' : ''}`} />
                    <span className="text-sm font-medium">
                      {isInWishlist(id) ? t('wishlist.added') : t('wishlist.add')}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => toggleCompare(id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
                    hasCompare(id)
                      ? 'bg-blue-50 border-blue-200 text-blue-600'
                      : 'border-gray-300 text-gray-700 hover:border-blue-200 hover:text-blue-500'
                  }`}
                  data-testid="product-compare-toggle"
                >
                  <Scale className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {hasCompare(id) ? t('compare.added') : t('compare.add')}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="border-t border-gray-200 p-8">
            <h2 className="text-2xl font-bold mb-6" data-testid="specifications-title">
              {t('product.specifications')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.specifications.light_transmission && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold">Svetlopriepustnosť:</span>
                  <span>{product.specifications.light_transmission}</span>
                </div>
              )}
              {product.specifications.uv_protection && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold">UV ochrana:</span>
                  <span>{product.specifications.uv_protection}</span>
                </div>
              )}
              {product.specifications.thickness && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold">Hrúbka:</span>
                  <span>{product.specifications.thickness}</span>
                </div>
              )}
              {product.specifications.roll_size && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold">Rozmery:</span>
                  <span>{product.specifications.roll_size}</span>
                </div>
              )}
              {product.specifications.color && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold">Farba:</span>
                  <span>{product.specifications.color}</span>
                </div>
              )}
              {product.specifications.adhesive_type && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold">Typ lepidla:</span>
                  <span>{product.specifications.adhesive_type}</span>
                </div>
              )}
              {product.specifications.warranty && (
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold">Záruka:</span>
                  <span>{product.specifications.warranty}</span>
                </div>
              )}
            </div>
          </div>

          {/* Features */}
          {getTranslation(product, 'features')?.length > 0 && (
            <div className="border-t border-gray-200 p-8">
              <h2 className="text-2xl font-bold mb-6" data-testid="features-title">
                {t('product.features')}
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {getTranslation(product, 'features').map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Cross-sell */}
          <CrossSell productId={id} />

          {/* Reviews */}
          <div id="reviews">
            <ProductReviews productId={id} />
          </div>
        </div>
        <RecentlyViewed excludeId={id} />
      </div>
    </div>
  );
};

export default ProductPage;